import type { Middleware } from "redux";

// Read these from the console to diagnose a freeze: localStorage.getItem("oc_freeze_log").
const LOG_KEY = "oc_freeze_log";
const BREADCRUMB_KEY = "oc_freeze_breadcrumb";
const STALL_MS = 3000;
const SLEEP_MS = 10 * 60 * 1000;
const SETTLE_MS = 10000;
const NOISY_ACTIONS = new Set(["SET_CLOCK", "TIMELINE_UPDATE_HOVERED_TIME"]);

const recentActions: string[] = [];
let breadcrumbAt: number | null = null;

const readJSON = <T>(key: string): T | null => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "null") as T | null;
  } catch {
    return null;
  }
};

const writeJSON = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be full or blocked; diagnostics must never break the app.
  }
};

const clearBreadcrumb = (): void => {
  breadcrumbAt = null;
  try {
    localStorage.removeItem(BREADCRUMB_KEY);
  } catch {
    // As above: blocked storage only loses diagnostics.
  }
};

/** Keeps the latest action types so a freeze report shows what led up to it. */
export const recentActionsMiddleware: Middleware = () => (next) => (action) => {
  const type = String(action?.type);
  if (action?.type != null && !NOISY_ACTIONS.has(type)) {
    recentActions.push(`${new Date().toISOString()} ${type}`);
    if (recentActions.length > 50) {
      recentActions.shift();
    }
  }
  return next(action);
};

/** Appends to the persisted freeze log, keeping the latest 10 entries. */
export const recordFreezeEvent = (kind: string, details: Record<string, unknown> = {}): void => {
  console.warn(`Freeze monitor: ${kind}`, details);
  const log = readJSON<object[]>(LOG_KEY) ?? [];
  writeJSON(LOG_KEY, [...log, { kind, at: new Date().toISOString(), ...details }].slice(-10));
};

/** Marks an interaction that could freeze the page; it is cleared once the page has stayed responsive. */
export const markInteraction = (label: string, details: Record<string, unknown> = {}): void => {
  breadcrumbAt = Date.now();
  writeJSON(BREADCRUMB_KEY, { label, details, at: new Date(breadcrumbAt).toISOString(), recentActions });
};

/** Logs main-thread stalls and any interaction the previous session never recovered from. Returns a stop function. */
export const startFreezeMonitor = (): (() => void) => {
  const leftover = readJSON<Record<string, unknown>>(BREADCRUMB_KEY);
  if (leftover) {
    recordFreezeEvent("previous session stopped responding", leftover);
    clearBreadcrumb();
  }

  let last = Date.now();
  // Hidden tabs throttle or freeze timers, so time spent hidden must not count as a stall.
  const resetBaseline = (): void => {
    last = Date.now();
  };
  document.addEventListener("visibilitychange", resetBaseline);
  const timer = setInterval(() => {
    const now = Date.now();
    const gap = now - last;
    // A gap past SLEEP_MS is the computer sleeping, which may not fire visibilitychange.
    if (document.visibilityState === "visible" && gap > STALL_MS && gap < SLEEP_MS) {
      recordFreezeEvent("main thread stalled", { ms: gap, recentActions: recentActions.slice(-15) });
    }
    if (breadcrumbAt != null && now - breadcrumbAt > SETTLE_MS) {
      clearBreadcrumb();
    }
    last = now;
  }, 1000);
  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", resetBaseline);
  };
};
